import React from 'react';
import { useWindow } from '@fluentui/react/lib/WindowProvider';
import { observer } from 'owa-mobx-react';
import { useResizeObserver } from 'owa-react-hooks/lib/useResizeObserver';
import {
    appModuleContainer,
    moduleContainer,
    bizChatInReadingPane,
} from './CopilotChatDrawerContainer.scss';
import { CopilotChatDrawer } from 'owa-copilot-chat-drawer';
import { isBizchatSidepaneEnabled } from 'owa-bizchat-shared/lib/utils/isBizchatSidepaneEnabled';
import {
    isCopilotChatDrawerShown,
    setAppCanvasWidthMutator,
    shouldShowBizChatInReadingPane,
} from 'owa-copilot-chat-drawer-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { trace } from 'owa-trace';
import classNames from 'owa-classnames';
import { getCurrentModule } from 'owa-app-module-store';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';

export interface CopilotChatDrawerContainerProps {
    children: React.ReactNode;
    containerStyles?: React.CSSProperties;
}

export const CopilotChatDrawerContainer: React.FunctionComponent<CopilotChatDrawerContainerProps> =
    observer(function CopilotChatDrawerContainer({ children, containerStyles }) {
        const mailboxInfo = getModuleContextMailboxInfo(); // DrawerContainer is only rendered in the main window
        const targetWindow = useWindow() ?? window;
        const isChatDrawerShown = isCopilotChatDrawerShown(targetWindow);
        const bizchatWaitingForInteraction = React.useRef<boolean>(true);
        const renderChatDrawer = React.useRef<boolean>(false);

        // We don't want to close the Chat Drawer if the check is undefined (pending) for the sticky open scenario
        // so we only hide it when it's explicitly false.
        const isBizchatDisabled = isBizchatSidepaneEnabled() === false;
        const showCopilotChatDrawer = isChatDrawerShown && !isBizchatDisabled;

        if (isChatDrawerShown) {
            bizchatWaitingForInteraction.current = false;
        }

        const isBizchatPreloading =
            isFeatureEnabled('fwk-copilot-iframe-preload') && bizchatWaitingForInteraction.current;

        const isBizchatCached =
            isFeatureEnabled('mon-copilot-bizchat-caching') &&
            !bizchatWaitingForInteraction.current;

        const shouldShowCopilotChatDrawer =
            (isBizchatPreloading || isBizchatCached || isChatDrawerShown) && !isBizchatDisabled;

        if (shouldShowCopilotChatDrawer && isFeatureEnabled('mon-copilot-bizchat-caching')) {
            // If ChatDrawer is rendered once, then we keep it rendered to preserve its caching state
            renderChatDrawer.current = true;
        }

        const copilotChatDrawerStyles = React.useMemo(() => {
            return containerStyles
                ? {
                      drawerContainer: { ...containerStyles },
                  }
                : undefined;
        }, [containerStyles]);
        trace.info(
            ` Copilot chat drawer state (APP MODULE):
            shouldShowCopilotChatDrawer: ${shouldShowCopilotChatDrawer}
            bizchatWaitingForInteraction: ${bizchatWaitingForInteraction.current},
            isBizchatPreloading: ${isBizchatPreloading},
            isBizchatCached: ${isBizchatCached},
            isCopilotChatDrawerShown: ${isChatDrawerShown},
        `,
            'iframeWrapper'
        );

        const appCanvasRef = React.useRef<HTMLDivElement>(null);

        // Update app canvas width when this container resizes
        const updateAppCanvasWidth = React.useCallback(
            (rect: DOMRectReadOnly) => {
                setAppCanvasWidthMutator(targetWindow, rect.width);
            },
            [targetWindow]
        );

        useResizeObserver('CopilotChatDrawerContainer', appCanvasRef, updateAppCanvasWidth);

        const currentModule = getCurrentModule();

        const overrideScenario = React.useMemo(() => {
            return shouldShowCopilotChatDrawer &&
                isFeatureEnabled('cal-copilot-override-scenario') &&
                currentModule === 'Calendar'
                ? 'CalendarSidepane'
                : undefined;
        }, [shouldShowCopilotChatDrawer, currentModule]);

        return (
            <div ref={appCanvasRef} className={appModuleContainer}>
                <div
                    className={classNames(
                        moduleContainer,
                        shouldShowBizChatInReadingPane(targetWindow) && bizChatInReadingPane
                    )}
                >
                    {children}
                </div>
                {(shouldShowCopilotChatDrawer || renderChatDrawer.current) && (
                    <CopilotChatDrawer
                        mailboxInfo={mailboxInfo}
                        targetWindow={targetWindow}
                        isVisible={showCopilotChatDrawer}
                        elementStyles={copilotChatDrawerStyles}
                        overrideScenario={overrideScenario}
                    />
                )}
            </div>
        );
    }, 'CopilotChatDrawerContainer');
